import './YesNoSegmented.scss';
import { KeyboardEvent, Ref } from 'react';
import clsx from 'clsx';
import { UnstyledButton } from '@mantine/core';
import { ReadonlyHint } from '../../atoms/ReadonlyIndicator';
import { useFieldA11y } from '../../hooks/useFieldA11y';

export interface IYesNoSegmentedProps {
    /** Ссылка на группу сегментов (role="radiogroup") */
    ref?: Ref<HTMLDivElement>;
    /** Значение формы: 1 (да) / 0 (нет) / null (не выбрано). */
    value: number | null;
    /** Выбор сегмента. */
    onChange: (value: number) => void;
    /** Сброс в null повторным кликом по выбранному сегменту. Без него активный
     *  сегмент по клику не сбрасывается. */
    onClear?: () => void;
    /** Подпись «да» (по умолчанию «Да»), может быть длинной. */
    trueText?: string;
    /** Подпись «нет» (по умолчанию «Нет»). */
    falseText?: string;
    disabled?: boolean;
    /** required-подсветка (пустое обязательное поле). */
    invalid?: boolean;
    ariaLabel?: string;
    testid?: string;
    /** FormControl показывает общую подсказку вместе с подписью. */
    readonlyHint?: boolean;
}

// Порядок сегментов как на макете: слева «Нет» (0), справа «Да» (1).
const idxOf = (value: number | null) => (value === 0 ? 0 : value === 1 ? 1 : -1);

/** Сегментированный «Да/Нет» с анимированным ползунком (кривая из ViewSwitcher).
 *  null = ползунок скрыт, оба сегмента нейтральны. Повторный клик по выбранному
 *  сегменту сбрасывает в null (если передан onClear). */
const YesNoSegmented = ({
    ref,
    value,
    onChange,
    onClear,
    trueText,
    falseText,
    disabled,
    invalid,
    ariaLabel,
    testid,
    readonlyHint = true,
}: IYesNoSegmentedProps) => {
    const segments = [
        { v: 0, label: falseText ?? 'Нет' },
        { v: 1, label: trueText ?? 'Да' },
    ];
    // подпись и сообщение группы формы (FormGroup) — имя и описание radiogroup
    const fieldA11y = useFieldA11y({ 'aria-label': ariaLabel });
    const activeIdx = idxOf(value);
    const clearable = !!onClear && !disabled;

    const handleClick = (v: number, active: boolean) => {
        if (active) {
            if (!disabled) onClear?.();
        } else {
            onChange(v);
        }
    };

    // Стрелки/Home/End переключают сегменты (событие ловим на кнопке в фокусе).
    const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
        if (disabled) return;
        if (e.key === 'ArrowLeft' || e.key === 'Home') {
            e.preventDefault();
            onChange(0);
        } else if (e.key === 'ArrowRight' || e.key === 'End') {
            e.preventDefault();
            onChange(1);
        }
    };

    const control = (
        <div
            className={clsx('yn-seg', {
                'yn-seg--empty': activeIdx < 0,
                'yn-seg--invalid': invalid,
                'yn-seg--disabled': disabled,
            })}
            ref={ref}
            role="radiogroup"
            aria-label={ariaLabel}
            {...fieldA11y}
            aria-required={invalid || undefined}
            aria-invalid={invalid || undefined}
            aria-disabled={disabled || undefined}
            data-testid={testid}
        >
            <span
                className="yn-seg__thumb"
                aria-hidden
                style={{ transform: `translateX(${Math.max(activeIdx, 0) * 100}%)` }}
            />
            {segments.map((s) => {
                const active = value === s.v;
                return (
                    <UnstyledButton
                        key={s.v}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        disabled={disabled}
                        tabIndex={active || (activeIdx < 0 && s.v === 0) ? 0 : -1}
                        className={clsx('yn-seg__btn', active && 'is-active', active && clearable && 'is-clearable')}
                        title={active && clearable ? 'Нажмите ещё раз, чтобы очистить' : undefined}
                        onClick={() => handleClick(s.v, active)}
                        onKeyDown={handleKeyDown}
                    >
                        {s.label}
                    </UnstyledButton>
                );
            })}
        </div>
    );

    return (
        <div className="yn-seg-field">
            {disabled && readonlyHint ? <ReadonlyHint>{control}</ReadonlyHint> : control}
        </div>
    );
};

export default YesNoSegmented;
